import { useEffect, useMemo, useRef, useState } from "react";
import http from "../../Api/http";
import { useTranslation } from "react-i18next";
import { useAuth } from "../AuthContext";
import {
    FaBusAlt,
    FaTruck,
    FaTimes,
    FaMapMarkerAlt,
    FaLayerGroup,
    FaExclamationTriangle,
    FaCheckCircle,
    FaClock,
    FaSearch,
    FaArrowUp,
    FaArrowDown,
    FaChevronLeft,
    FaChevronRight,
    FaSyncAlt,
    FaCarSide
} from "react-icons/fa";
import { motion, AnimatePresence } from "framer-motion";
import Util from "../../Util";

const BUS_TYPES = [2, 3, 4];
const TRUCK_TYPE = 1;

const numberFormat = (value, locale) =>
    new Intl.NumberFormat(locale).format(Number(value) || 0);

function FleetMonitor() {
    const { t, i18n } = useTranslation();
    const { user } = useAuth();

    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [error, setError] = useState(false);
    const [lastUpdated, setLastUpdated] = useState(null);

    const [search, setSearch] = useState("");
    const [filter, setFilter] = useState("all");
    const [sortBy, setSortBy] = useState("name");
    const [selectedStep, setSelectedStep] = useState(null);

    const dialogRef = useRef(null);

    const lang = (i18n.resolvedLanguage || i18n.language || "ar")
        .split("-")[0];

    const locale = {
        ar: "ar",
        en: "en",
        he: "he"
    }[lang] || "en";

    const dir = ["ar", "he"].includes(lang) ? "rtl" : "ltr";

    useEffect(() => {
        if (!user) {
            setData([]);
            setLoading(false);
            return undefined;
        }

        let active = true;
        let inFlight = false;

        const fetchData = async () => {
            if (inFlight) return;

            inFlight = true;

            if (active) {
                setRefreshing(true);
                setError(false);
            }

            try {
                const res = await http.getJsonAxios(
                    http.actions.Traffic.GetLocationsDetails,
                    { lang },
                    t
                );

                if (!active) return;

                if (res?.status === 200 && Array.isArray(res.data)) {
                    setData(res.data);
                    setLastUpdated(new Date());
                    setError(false);
                } else {
                    setError(true);
                }
            } catch (err) {
                console.error("Fleet dashboard request failed:", err);

                if (active) {
                    setError(true);
                }
            } finally {
                inFlight = false;

                if (active) {
                    setLoading(false);
                    setRefreshing(false);
                }
            }
        };

        fetchData();

        const configuredInterval = Number(
            window.API_URL?.interval_time
        );

        const interval = Number.isFinite(configuredInterval) &&
            configuredInterval >= 1000
            ? configuredInterval
            : 30000;

        const timer = window.setInterval(fetchData, interval);

        return () => {
            active = false;
            window.clearInterval(timer);
        };
    }, [user, lang, t]);

    useEffect(() => {
        if (!selectedStep) return undefined;

        const onKeyDown = (event) => {
            if (event.key === "Escape") {
                setSelectedStep(null);
            }
        };

        document.addEventListener("keydown", onKeyDown);

        return () => {
            document.removeEventListener("keydown", onKeyDown);
        };
    }, [selectedStep]);

    const steps = useMemo(() => {
        return (Array.isArray(data) ? data : [])
            .map((step) => {
                const types = Array.isArray(step?.BusTypes)
                    ? step.BusTypes
                    : [];

                const busTypes = types.filter(
                    (item) =>
                        BUS_TYPES.includes(Number(item?.BusTypeId))
                );

                const truckTypes = types.filter(
                    (item) =>
                        Number(item?.BusTypeId) === TRUCK_TYPE
                );

                const totalBuses = busTypes.reduce(
                    (sum, item) =>
                        sum + (Number(item.BusesCount) || 0),
                    0
                );

                const totalTrucks = truckTypes.reduce(
                    (sum, item) =>
                        sum + (Number(item.BusesCount) || 0),
                    0
                );

                const maxAllowed = [...busTypes, ...truckTypes].reduce(
                    (sum, item) =>
                        sum + (Number(item.MaxAllowed) || 0),
                    0
                );

                return {
                    ...step,
                    BusTypes: types,
                    busTypes,
                    truckTypes,
                    totalBuses,
                    totalTrucks,
                    totalVehicles: totalBuses + totalTrucks,
                    maxAllowed
                };
            })
            .filter(
                (step) =>
                    step.busTypes.length > 0 ||
                    step.truckTypes.length > 0
            );
    }, [data]);

    const totals = useMemo(() => {
        return steps.reduce(
            (result, step) => {
                result.buses += step.totalBuses;
                result.trucks += step.totalTrucks;

                if (step.totalVehicles > 0) {
                    result.activeSteps += 1;
                }

                if (
                    step.maxAllowed > 0 &&
                    step.totalVehicles >= step.maxAllowed
                ) {
                    result.warningSteps += 1;
                }

                return result;
            },
            {
                buses: 0,
                trucks: 0,
                activeSteps: 0,
                warningSteps: 0
            }
        );
    }, [steps]);

    const filteredSteps = useMemo(() => {
        const query = search.trim().toLocaleLowerCase(locale);

        return steps
            .filter((step) => {
                if (filter === "buses" && !step.busTypes.length) {
                    return false;
                }

                if (filter === "trucks" && !step.truckTypes.length) {
                    return false;
                }

                if (query) {
                    const name = String(step.StepName || "")
                        .toLocaleLowerCase(locale);

                    if (!name.includes(query)) {
                        return false;
                    }
                }

                return true;
            })
            .sort((a, b) => {
                if (sortBy === "count") {
                    return b.totalVehicles - a.totalVehicles;
                }

                if (sortBy === "capacity") {
                    const aPercent = a.maxAllowed
                        ? a.totalVehicles / a.maxAllowed
                        : 0;

                    const bPercent = b.maxAllowed
                        ? b.totalVehicles / b.maxAllowed
                        : 0;

                    return bPercent - aPercent;
                }

                return String(a.StepName || "").localeCompare(
                    String(b.StepName || ""),
                    locale
                );
            });
    }, [steps, search, filter, sortBy, locale]);

    const refreshNow = () => {
        // The interval handles automatic updates.
        // Use a dedicated refresh trigger here if manual refresh is needed.
        window.dispatchEvent(new Event("fleet-dashboard-refresh"));
    };

    return (
        <div
            dir={dir}
            className="min-h-screen bg-slate-50 text-slate-900 dark:bg-[#071426] dark:text-slate-100"
        >
            <div className="mx-auto max-w-[1600px] space-y-6 p-3 sm:p-5 lg:p-8">

                {/* Header */}
                <header className="flex flex-col gap-4 border-b border-slate-200 pb-5 dark:border-slate-800 sm:flex-row sm:items-center sm:justify-between">
                    <div className="flex items-center gap-3">
                        <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-blue-600 text-white shadow-lg shadow-blue-600/20">
                            <FaCarSide className="text-xl" />
                        </div>

                        <div className="min-w-0">
                            <h1 className="text-xl font-extrabold tracking-tight sm:text-2xl lg:text-3xl">
                                {t("fleet_monitoring", {
                                    defaultValue: t('fleet_monitoring')
                                })}
                            </h1>

                            <p className="mt-1 text-xs text-slate-500 dark:text-slate-400 sm:text-sm">
                                {t("fleet_dashboard_subtitle", {
                                    defaultValue:
                                        "Real-time fleet and location overview"
                                })}
                            </p>
                        </div>
                    </div>

                    <div className="flex flex-wrap items-center gap-3">
                        <div className="flex items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs font-semibold text-emerald-700 dark:border-emerald-900 dark:bg-emerald-950/40 dark:text-emerald-400">
                            <span
                                className={`h-2 w-2 rounded-full ${error
                                    ? "bg-rose-500"
                                    : "animate-pulse bg-emerald-500"
                                    }`}
                            />

                            {error
                                ? t("connectionError", {
                                    defaultValue: "Connection error"
                                })
                                : t("liveFeedActive", {
                                    defaultValue: "Live feed active"
                                })}
                        </div>

                        <button
                            type="button"
                            onClick={refreshNow}
                            disabled={refreshing}
                            className="inline-flex min-h-10 items-center gap-2 rounded-xl border border-blue-200 bg-white 
                            px-3 text-sm font-medium transition hover:bg-blue-100 
                            disabled:opacity-50 dark:border-slate-700 dark:bg-slate-900 dark:hover:bg-slate-800"
                        >
                            <FaSyncAlt className={refreshing ? "animate-spin" : ""} />
                            <span>{t("refresh", { defaultValue: "Refresh" })}</span>
                        </button>
                    </div>
                </header>

                {/* Connection error banner */}
                {error && (
                    <div className="flex items-start gap-3 rounded-2xl border border-rose-200 bg-rose-50 p-4 dark:border-rose-900 dark:bg-rose-950/30">
                        <FaExclamationTriangle className="mt-0.5 shrink-0 text-rose-500" />

                        <div>
                            <p className="font-semibold text-rose-700 dark:text-rose-300">
                                {t("connectionError", {
                                    defaultValue: "Connection error"
                                })}
                            </p>

                            <p className="mt-1 text-sm text-rose-600 dark:text-rose-400">
                                {t("failedToLoadData", {
                                    defaultValue:
                                        "Unable to refresh fleet data. Previously loaded data is preserved."
                                })}
                            </p>
                        </div>
                    </div>
                )}

                {/* KPI cards */}
                <section className="grid grid-cols-1 gap-4 xl:grid-cols-3">

                    {/* القسم الأول: توزيع الاسطول */}
                    <div className="min-w-0 xl:col-span-2">
                        {/* Fleet Distribution */}
                        <div className="col-span-2 xl:col-span-2 rounded-2xl border border-slate-200 bg-white p-4 
                        shadow-sm transition-shadow duration-300 hover:shadow-lg dark:border-slate-800 dark:bg-[#0c1d34] sm:p-6 xl:col-span-2">
                            <div className="flex items-center justify-between gap-2">
                                <div>
                                    <h2 className="text-base font-bold sm:text-lg">
                                        {t("fleetDistribution", {
                                            defaultValue: "Fleet distribution"
                                        })}
                                    </h2>

                                    <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                                        {t("fleetDistributionSubtitle", {
                                            defaultValue: "Vehicle distribution by type"
                                        })}
                                    </p>
                                </div>

                                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-blue-500/10 text-blue-500">
                                    <FaLayerGroup className="text-xl" />
                                </div>
                            </div>

                            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 sm:items-center">
                                <div className="flex justify-center">
                                    <FleetDonut
                                        buses={totals.buses}
                                        trucks={totals.trucks}
                                        locale={"en"}
                                        totalLabel={t("totalVehicles", {
                                            defaultValue: "Total vehicles"
                                        })}
                                        t={t}
                                    />
                                </div>

                                <div className="space-y-6">
                                    <DistributionRow
                                        label={t("buses")}
                                        value={totals.buses}
                                        total={totals.buses + totals.trucks}
                                        color="bg-blue-500"
                                        locale={locale}
                                    />

                                    <DistributionRow
                                        label={t("trucks")}
                                        value={totals.trucks}
                                        total={totals.buses + totals.trucks}
                                        color="bg-emerald-500"
                                        locale={locale}
                                    />

                                    <div className="border-t border-slate-100 pt-4 dark:border-slate-800">
                                        <div className="flex items-center justify-between gap-3 text-sm">
                                            <span className="text-slate-500 dark:text-slate-400">
                                                {t("totalVehicles", {
                                                    defaultValue: "Total vehicles"
                                                })}
                                            </span>

                                            <span className="text-lg font-extrabold tabular-nums">
                                                {numberFormat(
                                                    totals.buses + totals.trucks,
                                                    locale
                                                )}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* القسم الثاني: الإحصائيات */}
                    <div className="flex flex-col gap-3 xl:col-span-1">
                        <KpiCard className="flex-1"
                            title={t("buses")}
                            value={totals.buses}
                            icon={<FaBusAlt />}
                            color="blue"
                            loading={loading && data.length === 0}
                        />

                        <KpiCard className="flex-1"
                            title={t("trucks")}
                            value={totals.trucks}
                            icon={<FaTruck />}
                            color="emerald"
                            loading={loading && data.length === 0}
                        />

                        {/* <KpiCard className="flex-1"
                            title={t("activeLocations", {
                                defaultValue: "Active locations"
                            })}
                            value={totals.activeSteps}
                            icon={<FaMapMarkerAlt />}
                            color="violet"
                            loading={loading && data.length === 0}
                        /> */}

                        {/* <KpiCard
                        title={t("capacityWarnings", {
                            defaultValue: "Capacity warnings"
                        })}
                        value={totals.warningSteps}
                        icon={<FaExclamationTriangle />}
                        color="amber"
                        loading={loading && data.length === 0}
                    /> */}
                    </div>
                </section>

                {/* Fleet distribution */}
                {/* <section className="grid grid-cols-1 gap-4 xl:grid-cols-[minmax(0,1.5fr)_minmax(280px,0.8fr)]">

                     <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-[#0c1d34] sm:p-6">
                        <div className="mb-5 flex items-center justify-between">
                            <div>
                                <h2 className="text-base font-bold sm:text-lg">
                                    {t("locationStatus", {
                                        defaultValue: "Location status"
                                    })}
                                </h2>

                                <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                                    {t("capacityOverview", {
                                        defaultValue:
                                            "Vehicle capacity overview"
                                    })}
                                </p>
                            </div>

                            <FaClock className="text-xl text-violet-500" />
                        </div>

                        <div className="space-y-5">
                            <CapacitySummary
                                title={t("withinCapacity", {
                                    defaultValue: "Within capacity"
                                })}
                                count={steps.filter(
                                    (step) =>
                                        step.maxAllowed > 0 &&
                                        step.totalVehicles < step.maxAllowed
                                ).length}
                                total={steps.length}
                                color="bg-emerald-500"
                                locale={locale}
                            />

                            <CapacitySummary
                                title={t("atOrOverCapacity", {
                                    defaultValue: "At or over capacity"
                                })}
                                count={steps.filter(
                                    (step) =>
                                        step.maxAllowed > 0 &&
                                        step.totalVehicles >= step.maxAllowed
                                ).length}
                                total={steps.length}
                                color="bg-rose-500"
                                locale={locale}
                            />

                            <div className="rounded-xl bg-slate-50 p-3 text-xs leading-5 text-slate-500 dark:bg-slate-900 dark:text-slate-400">
                                {t("capacityNote", {
                                    defaultValue:
                                        "Capacity indicators are calculated from the MaxAllowed values returned by the API. Locations without a configured capacity are not included in these indicators."
                                })}
                            </div>
                        </div>
                    </div> 
                </section> */}

                {/* Locations */}
                <section className="rounded-2xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-[#0c1d34]">
                    <div className="border-b border-slate-200 p-4 dark:border-slate-800 sm:p-6">
                        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                            <div>
                                <h2 className="text-lg font-extrabold sm:text-xl">
                                    {t("allLocations", {
                                        defaultValue: "All locations"
                                    })}
                                </h2>

                                <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                                    {t("locationsSubtitle", {
                                        defaultValue:
                                            "Select a location to inspect its vehicles"
                                    })}
                                </p>
                            </div>

                            <div className="flex flex-col gap-3 sm:flex-row">
                                <div className="relative min-w-0 sm:min-w-[240px]">
                                    <FaSearch className={`absolute top-1/2 -translate-y-1/2 text-slate-400 ${dir === "rtl" ? "right-3" : "left-3"
                                        }`} />

                                    <input
                                        type="search"
                                        value={search}
                                        onChange={(event) =>
                                            setSearch(event.target.value)
                                        }
                                        placeholder={t("searchLocation", {
                                            defaultValue: "Search locations..."
                                        })}
                                        className={`h-11 w-full rounded-xl border border-slate-200 bg-slate-50 text-sm outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-500/10 dark:border-slate-700 dark:bg-slate-900 ${dir === "rtl"
                                            ? "pr-9 pl-3"
                                            : "pl-9 pr-3"
                                            }`}
                                    />
                                </div>

                                <select
                                    value={sortBy}
                                    onChange={(event) =>
                                        setSortBy(event.target.value)
                                    }
                                    aria-label={t("sortBy", {
                                        defaultValue: "Sort by"
                                    })}
                                    className="h-11 rounded-xl border border-slate-200 bg-slate-50 px-3 text-sm outline-none focus:border-blue-500 dark:border-slate-700 dark:bg-slate-900"
                                >
                                    <option value="name">
                                        {t("sortByName", {
                                            defaultValue: "Sort by name"
                                        })}
                                    </option>

                                    <option value="count">
                                        {t("sortByCount", {
                                            defaultValue: "Most vehicles"
                                        })}
                                    </option>

                                    <option value="capacity">
                                        {t("sortByCapacity", {
                                            defaultValue: "Highest capacity usage"
                                        })}
                                    </option>
                                </select>
                            </div>
                        </div>

                        <div className="mt-5 flex flex-wrap gap-2">
                            {[
                                {
                                    id: "all",
                                    label: t("all", {
                                        defaultValue: "All"
                                    })
                                },
                                {
                                    id: "buses",
                                    label: t("buses")
                                },
                                {
                                    id: "trucks",
                                    label: t("trucks")
                                }
                            ].map((item) => (
                                <button
                                    type="button"
                                    key={item.id}
                                    onClick={() => setFilter(item.id)}
                                    className={`rounded-xl px-4 py-2 text-sm font-semibold transition ${filter === item.id
                                        ? "bg-blue-600 text-white shadow-md shadow-blue-600/20"
                                        : "border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300 dark:hover:bg-slate-800"
                                        }`}
                                >
                                    {item.label}
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className="p-4 sm:p-6">
                        {loading && data.length === 0 ? (
                            <LoadingState t={t} />
                        ) : filteredSteps.length === 0 ? (
                            <div className="py-16 text-center">
                                <FaSearch className="mx-auto mb-4 text-3xl text-slate-300 dark:text-slate-700" />

                                <p className="font-semibold">
                                    {t("noResults", {
                                        defaultValue: "No locations found"
                                    })}
                                </p>

                                <p className="mt-1 text-sm text-slate-500">
                                    {t("tryDifferentSearch", {
                                        defaultValue:
                                            "Try changing your search or filter"
                                    })}
                                </p>
                            </div>
                        ) : (
                            <motion.div
                                layout
                                className="grid grid-cols-1 gap-4 md:grid-cols-2 2xl:grid-cols-3"
                            >
                                <AnimatePresence mode="popLayout">
                                    {filteredSteps.map((step) => (
                                        <StepCard
                                            key={step.StepID}
                                            step={step}
                                            onClick={() =>
                                                setSelectedStep(step)
                                            }
                                            t={t}
                                            locale={locale}
                                        />
                                    ))}
                                </AnimatePresence>
                            </motion.div>
                        )}
                    </div>
                </section>

                {/* Footer */}
                <footer className="flex flex-col gap-2 border-t border-slate-200 pt-4 text-xs text-slate-500 dark:border-slate-800 sm:flex-row sm:items-center sm:justify-between">
                    <span className="flex items-center gap-2">
                        <FaCheckCircle className="text-emerald-500" />

                        {t("systemMonitoring", {
                            defaultValue: "Fleet monitoring system"
                        })}
                    </span>

                    <span>
                        {lastUpdated
                            ? `${t("lastUpdated", {
                                defaultValue: "Last updated"
                            })}: ${lastUpdated.toLocaleTimeString(locale, {
                                hour: "2-digit",
                                minute: "2-digit",
                                second: "2-digit"
                            })}`
                            : t("waitingForData", {
                                defaultValue: "Waiting for data..."
                            })}
                    </span>
                </footer>
            </div>

            {/* Responsive details modal */}
            <AnimatePresence>
                {selectedStep && (
                    <StepDetailsModal
                        step={selectedStep}
                        onClose={() => setSelectedStep(null)}
                        dialogRef={dialogRef}
                        t={t}
                        locale={locale}
                        dir={dir}
                    />
                )}
            </AnimatePresence>
        </div>
    );
}

/* =========================================================
   KPI Card
========================================================= */

function KpiCard({ title, value, icon, color, loading }) {
    const colors = {
        blue: {
            icon: "bg-blue-500/10 text-blue-500",
            line: "from-blue-500 to-cyan-400",
            glow: "group-hover:shadow-blue-500/10"
        },
        emerald: {
            icon: "bg-emerald-500/10 text-emerald-500",
            line: "from-emerald-500 to-teal-400",
            glow: "group-hover:shadow-emerald-500/10"
        },
        violet: {
            icon: "bg-violet-500/10 text-violet-500",
            line: "from-violet-500 to-indigo-400",
            glow: "group-hover:shadow-violet-500/10"
        },
        amber: {
            icon: "bg-amber-500/10 text-amber-500",
            line: "from-amber-500 to-orange-400",
            glow: "group-hover:shadow-amber-500/10"
        }
    };

    const theme = colors[color] || colors.blue;

    return (
        <motion.div
            whileHover={{ y: -3 }}
            className={`group relative flex h-full flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white p-4 shadow-sm transition-shadow dark:border-slate-800 dark:bg-[#0c1d34] sm:p-5 ${theme.glow} hover:shadow-lg`}
        >
            <div className="flex items-start justify-between gap-2">
                <div className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl text-lg sm:h-12 sm:w-12 ${theme.icon}`}>
                    {icon}
                </div>

                <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 sm:text-xs">
                    {title}
                </span>
            </div>

            <div className="mt-3 text-2xl font-extrabold tracking-tight tabular-nums sm:text-3xl">
                {loading ? (
                    <span className="inline-block h-8 w-16 animate-pulse rounded-lg bg-slate-200 dark:bg-slate-800" />
                ) : (
                    <motion.span
                        key={value}
                        initial={{ opacity: 0.5, y: 4 }}
                        animate={{ opacity: 1, y: 0 }}
                    >
                        {numberFormat(value, "en")}
                    </motion.span>
                )}
            </div>

            <div className={`mt-auto absolute inset-x-0 bottom-0 h-1 bg-gradient-to-r ${theme.line}`} />
        </motion.div>
    );
}

/* =========================================================
   Fleet Distribution Donut
========================================================= */



function FleetDonut({ buses, trucks, locale, totalLabel, t }) {
    const total = buses + trucks;
    const busPercent = total ? (buses / total) * 100 : 0;
    const truckPercent = total ? (trucks / total) * 100 : 0;

    return (
        <div className="flex w-full flex-col items-center justify-center py-2">
            <div className="relative h-44 w-44 shrink-0 sm:h-48 sm:w-48">
                <div
                    className="absolute inset-0 rounded-full"
                    style={{
                        background: total
                            ? `conic-gradient(#3b82f6 0% ${busPercent}%, #10b981 ${busPercent}% 100% )`
                            : "#e2e8f0"
                    }}
                />

                <div className="absolute inset-[12px] rounded-full bg-white shadow-sm dark:bg-[#0c1d34]" />

                <div className="absolute inset-0 flex flex-col items-center justify-center">
                    <span className="text-3xl font-extrabold tracking-tight text-slate-900 dark:text-white sm:text-4xl">
                        {numberFormat(total, locale)}
                    </span>

                    <span className="mt-2 max-w-[120px] text-center text-xs font-medium text-slate-500 dark:text-slate-400">
                        {totalLabel}
                    </span>

                    <span className="mt-2 rounded-full bg-slate-100 px-3 py-1 text-[10px] font-bold text-slate-600 dark:bg-slate-800 dark:text-slate-300">
                        {total > 0 ? " 100% " : " 0% "}
                    </span>
                </div>
            </div>

            {/* <div className="mt-5 grid w-full grid-cols-2 gap-3">
                <div className="rounded-2xl border border-blue-100 bg-gradient-to-br from-blue-50 to-white p-3 text-center dark:border-blue-900/50 dark:from-blue-950/40 dark:to-[#0c1d34] sm:p-4">
                    <div className="mx-auto mb-2 flex h-9 w-9 items-center justify-center rounded-xl bg-blue-500/10 text-blue-600 dark:text-blue-400">
                        <FaBusAlt />
                    </div>

                    <p className="text-xs font-semibold text-slate-500 dark:text-slate-400">
                        {t("buses", { defaultValue: "Buses" })}
                    </p>

                    <p className="mt-1 text-xl font-extrabold tabular-nums text-slate-900 dark:text-white">
                        {numberFormat(buses, locale)}
                    </p>

                    <p className="mt-1 text-xs font-bold text-blue-600 dark:text-blue-400">
                        {numberFormat(busPercent, locale)}%
                    </p>
                </div>

                <div className="rounded-2xl border border-emerald-100 bg-gradient-to-br from-emerald-50 to-white p-3 text-center dark:border-emerald-900/50 dark:from-emerald-950/40 dark:to-[#0c1d34] sm:p-4">
                    <div className="mx-auto mb-2 flex h-9 w-9 items-center justify-center rounded-xl bg-emerald-500/10 text-emerald-600 dark:text-emerald-400">
                        <FaTruck />
                    </div>

                    <p className="text-xs font-semibold text-slate-500 dark:text-slate-400">
                        {t("trucks", { defaultValue: "Trucks" })}
                    </p>

                    <p className="mt-1 text-xl font-extrabold tabular-nums text-slate-900 dark:text-white">
                        {numberFormat(trucks, locale)}
                    </p>

                    <p className="mt-1 text-xs font-bold text-emerald-600 dark:text-emerald-400">
                        {numberFormat(truckPercent, locale)}%
                    </p>
                </div>
            </div> */}
        </div>
    );
}


function tSafeLabel(key, fallback) {
    const labels = {
        buses: fallback === "Buses" ? "Buses" : fallback,
        trucks: fallback === "Trucks" ? "Trucks" : fallback
    };

    return labels[key] || fallback;
}


/* =========================================================
   Distribution Row
========================================================= */

function DistributionRow({ label, value, total, color, locale }) {
    const percentage = total
        ? Math.round((value / total) * 100)
        : 0;

    return (
        <div>
            <div className="mb-2 flex items-center justify-between gap-3">
                <div className="flex items-center gap-2 text-sm font-medium">
                    <span className={`h-2.5 w-2.5 rounded-full ${color}`} />
                    {label}
                </div>

                <span className="text-sm font-bold tabular-nums">
                    {numberFormat(value, locale)}
                    <span className="ms-2 text-xs font-normal text-slate-500">
                        {percentage}%
                    </span>
                </span>
            </div>

            <div
                className="h-2 overflow-hidden rounded-full bg-slate-100 dark:bg-slate-800"
                role="progressbar"
                aria-valuenow={percentage}
                aria-valuemin={0}
                aria-valuemax={100}
            >
                <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: `${percentage}%` }}
                    transition={{ duration: 0.7 }}
                    className={`h-full rounded-full ${color}`}
                />
            </div>
        </div>
    );
}

/* =========================================================
   Capacity Summary
========================================================= */

function CapacitySummary({ title, count, total, color, locale }) {
    const percentage = total
        ? Math.round((count / total) * 100)
        : 0;

    return (
        <div>
            <div className="mb-2 flex items-center justify-between gap-3">
                <span className="text-sm font-medium">{title}</span>

                <span className="text-sm font-bold tabular-nums">
                    {numberFormat(count, locale)}
                    <span className="ms-1 text-xs font-normal text-slate-500">
                        / {numberFormat(total, locale)}
                    </span>
                </span>
            </div>

            <div className="h-2 overflow-hidden rounded-full bg-slate-100 dark:bg-slate-800">
                <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: `${percentage}%` }}
                    className={`h-full rounded-full ${color}`}
                    transition={{ duration: 0.7 }}
                />
            </div>
        </div>
    );
}

/* =========================================================
   Step Card
========================================================= */

function StepCard({ step, onClick, t, locale }) {
    const total = step.totalVehicles;
    const maximum = step.maxAllowed;

    const percentage = maximum > 0
        ? Math.round((total / maximum) * 100)
        : null;

    const critical = maximum > 0 && total >= maximum;
    const empty = total === 0;

    let criticalText = t("capacityReached", { defaultValue: "Capacity reached" });
    if (step && (step.StepID === 10 || step.StepID === 11 || step.StepID === 12 || step.StepID === 60)) {
        criticalText = t("obstruction", { defaultValue: "Obstruction" });
    }

    const status = critical
        ? {
            text: criticalText,
            color: "bg-rose-50 text-rose-700 ring-rose-200 dark:bg-rose-950/40 dark:text-rose-300 dark:ring-rose-900"
        }
        : empty
            ? {
                text: t("noVehicles", {
                    defaultValue: "No vehicles"
                }),
                color: "bg-slate-100 text-slate-600 ring-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:ring-slate-700"
            }
            : {
                text: t("normalStatus", {
                    defaultValue: "Normal"
                }),
                color: "bg-emerald-50 text-emerald-700 ring-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300 dark:ring-emerald-900"
            };

    const hasBuses = step.busTypes.length > 0;
    const hasTrucks = step.truckTypes.length > 0;

    return (
        <motion.button
            layout
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.97 }}
            whileHover={{ y: -3 }}
            whileTap={{ scale: 0.99 }}
            type="button"
            onClick={onClick}
            className="group w-full min-w-0 rounded-2xl border border-slate-200 bg-white p-4 text-start shadow-sm transition hover:border-blue-300 hover:shadow-lg hover:shadow-blue-500/5 focus:outline-none focus:ring-2 focus:ring-blue-500/30 dark:border-slate-800 dark:bg-[#0c1d34] dark:hover:border-blue-700 sm:p-5"
        >
            <div className="flex items-start justify-between gap-3">
                <div className="flex min-w-0 items-start gap-3">
                    <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-blue-50 text-blue-600 dark:bg-blue-950/50 dark:text-blue-400">
                        <FaMapMarkerAlt className="text-lg" />
                    </div>

                    <div className="min-w-0">
                        <h3 className="break-words text-sm font-bold leading-6 sm:text-base">
                            {Util.fixStepName(step.StepName) || t("location", {
                                defaultValue: "Location"
                            })}
                        </h3>

                        <div className="mt-1 flex flex-wrap gap-2">
                            {hasBuses && (
                                <span className="inline-flex items-center gap-1 text-xs text-blue-600 dark:text-blue-400">
                                    <FaBusAlt />
                                    {t("buses")}
                                </span>
                            )}

                            {hasTrucks && (
                                <span className="inline-flex items-center gap-1 text-xs text-emerald-600 dark:text-emerald-400">
                                    <FaTruck />
                                    {t("trucks")}
                                </span>
                            )}
                        </div>
                    </div>
                </div>

                <span className={`shrink-0 rounded-lg px-2 py-1 text-[10px] font-bold ring-1 sm:text-xs ${status.color}`}>
                    {status.text}
                </span>
            </div>

            <div className="mt-5 flex items-end justify-between gap-3">
                <div>
                    <div className="text-2xl font-extrabold tabular-nums sm:text-3xl">
                        {numberFormat(total, locale)}
                    </div>

                    <div className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                        {t("totalVehicles", {
                            defaultValue: "Total vehicles"
                        })}
                    </div>
                </div>

                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-slate-100 text-slate-500 transition group-hover:bg-blue-600 group-hover:text-white dark:bg-slate-800 dark:text-slate-300">
                    {<FaChevronRight className="rtl:rotate-180" />}
                </div>
            </div>

            {/* {percentage !== null && (
                <div className="mt-5">
                    <div className="mb-2 flex items-center justify-between gap-2 text-xs">
                        <span className="text-slate-500 dark:text-slate-400">
                            {t("capacityUsage", {
                                defaultValue: "Capacity usage"
                            })}
                        </span>

                        <span className={`font-bold tabular-nums ${critical
                                ? "text-rose-500"
                                : "text-slate-600 dark:text-slate-300"
                            }`}>
                            {percentage}%
                        </span>
                    </div>

                    <div className="h-2 overflow-hidden rounded-full bg-slate-100 dark:bg-slate-800">
                        <motion.div
                            initial={{ width: 0 }}
                            animate={{
                                width: `${Math.min(percentage, 100)}%`
                            }}
                            transition={{ duration: 0.6 }}
                            className={`h-full rounded-full ${critical
                                    ? "bg-rose-500"
                                    : percentage >= 80
                                        ? "bg-amber-500"
                                        : "bg-emerald-500"
                                }`}
                        />
                    </div>

                    {percentage > 100 && (
                        <p className="mt-2 text-xs font-semibold text-rose-500">
                            {t("overCapacityBy", {
                                defaultValue: "Over capacity by"
                            })}{" "}
                            {percentage - 100}%
                        </p>
                    )}
                </div>
            )} */}

            {/* <div className="mt-4 flex items-center justify-between border-t border-slate-100 pt-3 text-xs dark:border-slate-800">
                <span className="text-slate-500 dark:text-slate-400">
                    {t("viewDetails", {
                        defaultValue: "View details"
                    })}
                </span>

                <span className="font-semibold text-blue-600 transition group-hover:underline dark:text-blue-400">
                    {t("viewDetailsAction", {
                        defaultValue: "Open"
                    })}
                </span>
            </div> */}
        </motion.button>
    );
}

/* =========================================================
   Loading
========================================================= */

function LoadingState({ t }) {
    return (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 2xl:grid-cols-3">
            {Array.from({ length: 6 }).map((_, index) => (
                <div
                    key={index}
                    className="animate-pulse rounded-2xl border border-slate-200 p-5 dark:border-slate-800"
                >
                    <div className="h-4 w-2/3 rounded bg-slate-200 dark:bg-slate-800" />
                    <div className="mt-5 h-8 w-1/3 rounded bg-slate-200 dark:bg-slate-800" />
                    <div className="mt-5 h-2 rounded bg-slate-200 dark:bg-slate-800" />
                </div>
            ))}

            <p className="col-span-full text-center text-sm text-slate-500">
                {t("loadingData", {
                    defaultValue: "Loading data..."
                })}
            </p>
        </div>
    );
}

/* =========================================================
   Details Modal
========================================================= */

function StepDetailsModal({ step, onClose, dialogRef, t, locale, dir }) {
    const [detailsSearch, setDetailsSearch] = useState("");

    const busDetails = useMemo(() => {
        const allDetails = [
            ...(step.busTypes || []),
            ...(step.truckTypes || [])
        ].flatMap((type) =>
            Array.isArray(type.BusDetails) ? type.BusDetails : []
        );

        const query = detailsSearch.trim().toLocaleLowerCase(locale);

        return allDetails
            .filter((bus) => {
                if (!query) return true;

                return [
                    bus.BusNo,
                    bus.BusType,
                    bus.DriverName
                ].some((value) =>
                    String(value || "")
                        .toLocaleLowerCase(locale)
                        .includes(query)
                );
            })
            .sort((a, b) => {
                const timeA = a.ArrivalTime
                    ? new Date(a.ArrivalTime).getTime()
                    : 0;

                const timeB = b.ArrivalTime
                    ? new Date(b.ArrivalTime).getTime()
                    : 0;

                return timeA - timeB;
            });
    }, [step, detailsSearch, locale]);

    return (
        <motion.div
            dir={dir}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onMouseDown={(event) => {
                if (event.target === event.currentTarget) {
                    onClose();
                }
            }}
            className="fixed inset-0 z-50 flex items-end justify-center bg-slate-950/60 p-0 backdrop-blur-sm sm:items-center sm:p-3 md:p-5"
        >
            <motion.div
                ref={dialogRef}
                role="dialog"
                aria-modal="true"
                aria-labelledby="fleet-step-dialog-title"
                initial={{ opacity: 0, y: 30, scale: 0.98 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 20, scale: 0.98 }}
                transition={{ duration: 0.2 }}
                className="flex max-h-[94dvh] w-full min-w-0 flex-col overflow-hidden rounded-t-2xl border border-slate-200 bg-white shadow-2xl dark:border-slate-800 dark:bg-[#0c1d34] sm:max-h-[88dvh] sm:max-w-3xl sm:rounded-2xl lg:max-w-5xl"
            >
                {/* Header */}
                <header className="flex shrink-0 items-center justify-between gap-3 border-b border-slate-200 p-3 sm:p-5 dark:border-slate-800">
                    <div className="flex min-w-0 items-center gap-3">
                        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-blue-500/10 text-blue-500 sm:h-12 sm:w-12">
                            <FaLayerGroup className="text-lg sm:text-xl" />
                        </div>

                        <div className="min-w-0">
                            <h2
                                id="fleet-step-dialog-title"
                                className="break-words text-sm font-extrabold sm:text-lg md:text-xl"
                            >
                                {Util.fixStepName(step.StepName)}
                            </h2>

                            <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                                {numberFormat(step.totalVehicles || 0, locale)}{" "}
                                {t("totalVehicles", {
                                    defaultValue: "Total vehicles"
                                })}
                            </p>
                        </div>
                    </div>

                    <button
                        type="button"
                        onClick={onClose}
                        aria-label={t("close", {
                            defaultValue: "Close"
                        })}
                        className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl text-slate-500 transition hover:bg-slate-100 hover:text-slate-900 dark:hover:bg-slate-800 dark:hover:text-white sm:h-10 sm:w-10"
                    >
                        <FaTimes />
                    </button>
                </header>

                {/* Search */}
                <div className="shrink-0 border-b border-slate-200 p-3 sm:p-4 dark:border-slate-800">
                    <div className="relative">
                        <FaSearch
                            className={`absolute top-1/2 -translate-y-1/2 text-slate-400 ${dir === "rtl" ? "right-3" : "left-3"
                                }`}
                        />

                        <input
                            type="search"
                            value={detailsSearch}
                            onChange={(event) =>
                                setDetailsSearch(event.target.value)
                            }
                            placeholder={t("searchVehicles", {
                                defaultValue:
                                    "Search bus number, type or driver..."
                            })}
                            className={`h-11 w-full rounded-xl border border-slate-200 bg-slate-50 text-sm outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-500/10 dark:border-slate-700 dark:bg-slate-900 ${dir === "rtl"
                                    ? "pr-10 pl-3"
                                    : "pl-10 pr-3"
                                }`}
                        />
                    </div>
                </div>

                {/* Responsive vehicle details */}
                <div className="min-h-0 flex-1 overflow-auto p-3 sm:p-4 lg:p-0">
                    {busDetails.length === 0 ? (
                        <div className="p-10 text-center text-sm text-slate-500">
                            {t("noTransitRecords", {
                                defaultValue:
                                    "No transit records found for this location."
                            })}
                        </div>
                    ) : (
                        <>
                            {/* Mobile and tablet: cards */}
                            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:hidden">
                                {busDetails.map((bus, index) => {
                                    const arrival = bus.ArrivalTime
                                        ? new Date(bus.ArrivalTime)
                                        : null;

                                    const arrivalTime =
                                        arrival && !Number.isNaN(arrival.getTime())
                                            ? arrival.toLocaleTimeString(locale, {
                                                hour: "2-digit",
                                                minute: "2-digit",
                                                hour12: false
                                            })
                                            : "—";

                                    return (
                                        <article
                                            key={
                                                bus.BusId ??
                                                bus.BusID ??
                                                bus.BusNo ??
                                                `${bus.ArrivalTime}-${index}`
                                            }
                                            className="overflow-hidden rounded-xl border border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-900/60"
                                        >
                                            <div className="flex items-center justify-between gap-3 border-b border-slate-100 bg-slate-50 p-3 dark:border-slate-800 dark:bg-slate-800/50">
                                                <div className="min-w-0">
                                                    <p className="text-xs text-slate-500">
                                                        {t("busNo", {
                                                            defaultValue: "Bus number"
                                                        })}
                                                    </p>
                                                    <p className="mt-1 break-words font-extrabold text-blue-600 dark:text-blue-400">
                                                        {bus.BusNo || "—"}
                                                    </p>
                                                </div>

                                                <span className="shrink-0 rounded-lg bg-slate-200 px-2 py-1 text-xs font-bold text-slate-600 dark:bg-slate-700 dark:text-slate-200">
                                                    {numberFormat(index + 1, locale)}
                                                </span>
                                            </div>

                                            <div className="grid grid-cols-1 gap-3 p-3 min-[380px]:grid-cols-2">
                                                <div className="min-w-0">
                                                    <p className="text-xs text-slate-500 dark:text-slate-400">
                                                        {t("driverName")}
                                                    </p>
                                                    <p className="mt-1 break-words text-sm font-medium">
                                                        {Util.getShortName(bus.DriverName) || "—"}
                                                    </p>
                                                </div>

                                                <div className="min-w-0">
                                                    <p className="text-xs text-slate-500 dark:text-slate-400">
                                                        {t("arrivalTime")}
                                                    </p>
                                                    <p className="mt-1 text-sm font-medium tabular-nums">
                                                        {arrivalTime}
                                                    </p>
                                                </div>

                                                <div className="min-w-0 min-[380px]:col-span-2">
                                                    <p className="text-xs text-slate-500 dark:text-slate-400">
                                                        {t("duration")}
                                                    </p>
                                                    <p className="mt-1 break-words text-sm font-medium">
                                                        {bus.Duration ?? "—"}
                                                    </p>
                                                </div>
                                            </div>
                                        </article>
                                    );
                                })}
                            </div>

                            {/* Desktop: original table rows */}
                            <div className="hidden lg:block">
                                <table className="w-full min-w-[600px] text-start text-sm">
                                    <thead className="sticky top-0 z-10 bg-slate-100 text-xs font-bold text-slate-600 dark:bg-slate-900 dark:text-slate-300">
                                        <tr>
                                            <th className="px-4 py-4 text-center">#</th>
                                            <th className="px-4 py-4">{t("busNo")}</th>

                                            {/* <th className="px-4 py-4">
                                {t("busType")}
                            </th> */}

                                            <th className="px-4 py-4">{t("driverName")}</th>
                                            <th className="px-4 py-4">{t("arrivalTime")}</th>
                                            <th className="px-4 py-4">{t("duration")}</th>
                                        </tr>
                                    </thead>

                                    <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                                        {busDetails.map((bus, index) => {
                                            const arrival = bus.ArrivalTime
                                                ? new Date(bus.ArrivalTime)
                                                : null;

                                            const arrivalTime =
                                                arrival && !Number.isNaN(arrival.getTime())
                                                    ? arrival.toLocaleTimeString(locale, {
                                                        hour: "2-digit",
                                                        minute: "2-digit",
                                                        hour12: false
                                                    })
                                                    : "—";

                                            return (
                                                <tr
                                                    key={
                                                        bus.BusId ??
                                                        bus.BusID ??
                                                        bus.BusNo ??
                                                        `${bus.ArrivalTime}-${index}`
                                                    }
                                                    className="transition hover:bg-blue-50/50 dark:hover:bg-blue-950/20"
                                                >
                                                    <td className="px-4 py-4 text-center text-xs text-slate-500">
                                                        {numberFormat(index + 1, locale)}
                                                    </td>

                                                    <td className="px-4 py-4 font-bold text-blue-600 dark:text-blue-400">
                                                        {bus.BusNo || "—"}
                                                    </td>

                                                    {/* <td className="px-4 py-4">
                                        {bus.BusType || "—"}
                                    </td> */}

                                                    <td className="px-4 py-4">
                                                        {Util.getShortName(bus.DriverName) || "—"}
                                                    </td>

                                                    <td className="whitespace-nowrap px-4 py-4 tabular-nums">
                                                        {arrivalTime}
                                                    </td>

                                                    <td className="whitespace-nowrap px-4 py-4">
                                                        {bus.Duration ?? "—"}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </>
                    )}
                </div>


                {/* Footer */}
                <footer className="flex shrink-0 items-center justify-between gap-3 border-t border-slate-200 bg-white p-3 dark:border-slate-800 dark:bg-[#0c1d34] sm:px-5 sm:py-4">
                    <span className="text-xs font-medium text-slate-500 dark:text-slate-400 sm:text-sm">
                        {numberFormat(busDetails.length, locale)}{" "}
                        {t("records", {
                            defaultValue: "records"
                        })}
                    </span>

                    <button
                        type="button"
                        onClick={onClose}
                        className="rounded-xl bg-blue-600 px-5 py-2.5 text-sm font-bold text-white transition hover:bg-blue-700 active:scale-[0.98]"
                    >
                        {t("close", {
                            defaultValue: "Close"
                        })}
                    </button>
                </footer>
            </motion.div>
        </motion.div>
    );
}

export default FleetMonitor;